// Modern UI Component Library for NeuroPsyMap
// Reusable components for building the interface

// Note: CSS is loaded via index.html

export interface ButtonConfig {
  label: string;
  variant?: 'primary' | 'success' | 'warning' | 'danger' | 'ghost';
  size?: 'small' | 'medium' | 'large';
  icon?: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

export interface CardConfig {
  title?: string;
  icon?: string;
  collapsible?: boolean;
  content?: HTMLElement | string;
  className?: string;
  onCollapse?: (expanded: boolean) => void;
  defaultExpanded?: boolean;
}

export interface CollapsibleSectionConfig {
  title: string;
  icon?: string;
  content: HTMLElement | string;
  defaultExpanded?: boolean;
  className?: string;
}

/**
 * Create a modern button with consistent styling
 */
export function createButton(config: ButtonConfig): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'btn';
  
  // Add variant
  if (config.variant) {
    btn.classList.add(`btn-${config.variant}`);
  } else {
    btn.classList.add('btn-primary');
  }
  
  // Add size
  if (config.size) {
    btn.classList.add(`btn-${config.size}`);
  }
  
  // Add custom class
  if (config.className) {
    btn.classList.add(config.className);
  }
  
  // Add icon
  if (config.icon) {
    btn.innerHTML = config.icon + ' ';
  }
  
  // Add label
  const label = document.createTextNode(config.label);
  btn.appendChild(label);
  
  // Add click handler
  if (config.onClick) {
    btn.addEventListener('click', config.onClick);
  }
  
  // Handle disabled state
  if (config.disabled) {
    btn.disabled = true;
    btn.style.opacity = '0.5';
    btn.style.cursor = 'not-allowed';
  }
  
  return btn;
}

/**
 * Create a card component
 */
export function createCard(config: CardConfig): HTMLDivElement {
  const card = document.createElement('div');
  card.className = 'card';
  
  if (config.className) {
    card.classList.add(config.className);
  }
  
  // Create header if title provided
  if (config.title) {
    const header = document.createElement('div');
    header.className = 'card-header';
    
    const title = document.createElement('h3');
    title.className = 'card-title';
    
    if (config.icon) {
      title.innerHTML = config.icon + ' ';
    }
    
    const titleText = document.createTextNode(config.title);
    title.appendChild(titleText);
    header.appendChild(title);
    
    // Add collapse toggle if collapsible
    if (config.collapsible) {
      const toggle = createButton({
        label: '▼',
        variant: 'ghost',
        size: 'small',
        className: 'collapse-toggle',
        onClick: () => {
          const content = card.querySelector('.card-body') as HTMLElement;
          if (content) {
            const isExpanded = !content.classList.contains('hidden');
            content.classList.toggle('hidden', isExpanded);
            toggle.textContent = isExpanded ? '▶' : '▼';
            if (config.onCollapse) {
              config.onCollapse(!isExpanded);
            }
          }
        }
      });
      header.appendChild(toggle);
    }
    
    card.appendChild(header);
  }
  
  // Create body
  if (config.content) {
    const body = document.createElement('div');
    body.className = 'card-body';
    
    if (typeof config.content === 'string') {
      body.innerHTML = config.content;
    } else {
      body.appendChild(config.content);
    }
    
    card.appendChild(body);
  }
  
  return card;
}

/**
 * Create a collapsible section
 */
export function createCollapsibleSection(config: CollapsibleSectionConfig): HTMLDivElement {
  const section = document.createElement('div');
  section.className = 'collapsible-section';
  
  if (config.className) {
    section.classList.add(config.className);
  }
  
  let isExpanded = config.defaultExpanded ?? false;
  
  // Create header
  const header = document.createElement('div');
  header.className = 'collapsible-header';
  
  if (isExpanded) {
    header.classList.add('active');
  }
  
  const title = document.createElement('div');
  title.className = 'collapsible-title';
  
  if (config.icon) {
    const iconSpan = document.createElement('span');
    iconSpan.innerHTML = config.icon + ' ';
    title.appendChild(iconSpan);
  }
  
  const titleText = document.createTextNode(config.title);
  title.appendChild(titleText);
  header.appendChild(title);
  
  const icon = document.createElement('span');
  icon.className = 'collapsible-icon';
  icon.textContent = '▼';
  if (isExpanded) {
    icon.classList.add('expanded');
  }
  header.appendChild(icon);
  
  // Create content
  const content = document.createElement('div');
  content.className = 'collapsible-content';
  
  if (isExpanded) {
    content.classList.add('expanded');
  }
  
  if (typeof config.content === 'string') {
    content.innerHTML = config.content;
  } else {
    content.appendChild(config.content);
  }
  
  // Toggle on header click
  header.addEventListener('click', () => {
    isExpanded = !isExpanded;
    
    if (isExpanded) {
      header.classList.add('active');
      icon.classList.add('expanded');
      content.classList.add('expanded');
    } else {
      header.classList.remove('active');
      icon.classList.remove('expanded');
      content.classList.remove('expanded');
    }
  });
  
  section.appendChild(header);
  section.appendChild(content);
  
  return section;
}

/**
 * Create a stat item
 */
export function createStatItem(label: string, value: string | number, className?: string): HTMLDivElement {
  const stat = document.createElement('div');
  stat.className = 'stat-item';
  
  if (className) {
    stat.classList.add(className);
  }
  
  const labelElement = document.createElement('span');
  labelElement.className = 'stat-label';
  labelElement.textContent = label;
  
  const valueElement = document.createElement('span');
  valueElement.className = 'stat-value';
  valueElement.textContent = String(value);
  
  stat.appendChild(labelElement);
  stat.appendChild(valueElement);
  
  return stat;
}

/**
 * Create a large stat display
 */
export function createLargeStat(label: string, value: string | number): HTMLDivElement {
  const stat = document.createElement('div');
  stat.className = 'stat-large';
  
  const labelElement = document.createElement('span');
  labelElement.className = 'stat-label';
  labelElement.textContent = label;
  
  const valueElement = document.createElement('span');
  valueElement.className = 'stat-value';
  valueElement.textContent = String(value);
  
  stat.appendChild(labelElement);
  stat.appendChild(valueElement);
  
  return stat;
}

/**
 * Create a progress bar
 */
export function createProgressBar(percentage: number, label?: string, variant: 'primary' | 'success' = 'primary'): HTMLDivElement {
  const container = document.createElement('div');
  container.className = 'progress-bar-container';
  
  if (label) {
    const labelElement = document.createElement('span');
    labelElement.className = 'stat-label';
    labelElement.textContent = label;
    container.appendChild(labelElement);
  }
  
  const barWrapper = document.createElement('div');
  barWrapper.style.display = 'flex';
  barWrapper.style.alignItems = 'center';
  barWrapper.style.gap = '8px';
  
  const bar = document.createElement('div');
  bar.className = 'progress-bar';
  if (variant === 'success') {
    bar.classList.add('progress-bar-success');
  }
  bar.style.width = `${Math.min(100, Math.max(0, percentage))}%`;
  barWrapper.appendChild(bar);
  
  const percentageText = document.createElement('span');
  percentageText.className = 'text-sm text-secondary';
  percentageText.textContent = `${percentage.toFixed(0)}%`;
  barWrapper.appendChild(percentageText);
  
  container.appendChild(barWrapper);
  
  return container;
}

/**
 * Create a header element
 */
export function createHeader(title: string, actions?: HTMLElement[]): HTMLDivElement {
  const header = document.createElement('div');
  header.className = 'header';
  
  const titleElement = document.createElement('div');
  titleElement.className = 'header-title';
  titleElement.textContent = title;
  header.appendChild(titleElement);
  
  if (actions && actions.length > 0) {
    const actionsContainer = document.createElement('div');
    actionsContainer.className = 'header-actions';
    
    actions.forEach(action => {
      actionsContainer.appendChild(action);
    });
    
    header.appendChild(actionsContainer);
  }
  
  return header;
}

/**
 * Create an info bar
 */
export function createInfoBar(items: HTMLElement[]): HTMLDivElement {
  const infoBar = document.createElement('div');
  infoBar.className = 'info-bar';
  
  items.forEach(item => {
    item.classList.add('info-bar-item');
    infoBar.appendChild(item);
  });
  
  return infoBar;
}

/**
 * Helper to create info bar text
 */
export function createInfoBarText(html: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.innerHTML = html;
  return span;
}

/**
 * Create a modal
 */
export function createModal(content: HTMLElement | string, onClose?: () => void): HTMLDivElement {
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  
  const modal = document.createElement('div');
  modal.className = 'modal';
  
  if (typeof content === 'string') {
    modal.innerHTML = content;
  } else {
    modal.appendChild(content);
  }
  
  backdrop.appendChild(modal);
  
  // Close on backdrop click
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) {
      backdrop.remove();
      if (onClose) onClose();
    }
  });
  
  return backdrop;
}

/**
 * Update sidebar content with new structure
 */
export function updateSidebarContent(sidebar: HTMLElement, sections: HTMLElement[]): void {
  sidebar.innerHTML = '';
  sections.forEach(section => {
    sidebar.appendChild(section);
  });
}

/**
 * Create a breathing system visual component
 */
export function createBreathingVisual(o2Level: number, co2Level: number, phase: string): HTMLDivElement {
  const container = document.createElement('div');
  container.style.padding = 'var(--space-4)';
  
  const statsGrid = document.createElement('div');
  statsGrid.className = 'grid grid-cols-2';
  statsGrid.style.gap = 'var(--space-3)';
  
  // O2 Level
  const o2Card = document.createElement('div');
  o2Card.className = 'card';
  o2Card.innerHTML = `
    <div style="text-align: center;">
      <div class="text-xs text-tertiary">O₂</div>
      <div class="text-2xl font-bold" style="color: var(--breathing-o2);">${(o2Level * 100).toFixed(0)}%</div>
      <div class="progress-bar-container" style="margin-top: var(--space-2);">
        <div class="progress-bar" style="width: ${o2Level * 100}%; background: var(--breathing-o2);"></div>
      </div>
    </div>
  `;
  statsGrid.appendChild(o2Card);
  
  // CO2 Level
  const co2Card = document.createElement('div');
  co2Card.className = 'card';
  co2Card.innerHTML = `
    <div style="text-align: center;">
      <div class="text-xs text-tertiary">CO₂</div>
      <div class="text-2xl" style="color: var(--breathing-co2);">${(co2Level * 100).toFixed(0)}%</div>
      <div class="progress-bar-container" style="margin-top: var(--space-2);">
        <div class="progress-bar" style="width: ${co2Level * 100}%; background: var(--breathing-co2);"></div>
      </div>
    </div>
  `;
  statsGrid.appendChild(co2Card);
  
  container.appendChild(statsGrid);
  
  // Phase indicator
  const phaseIndicator = document.createElement('div');
  phaseIndicator.style.marginTop = 'var(--space-4)';
  phaseIndicator.style.textAlign = 'center';
  phaseIndicator.style.padding = 'var(--space-3)';
  phaseIndicator.style.background = phase === 'inhale' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)';
  phaseIndicator.style.borderRadius = 'var(--radius-md)';
  phaseIndicator.style.fontSize = 'var(--font-size-lg)';
  phaseIndicator.innerHTML = `🫁 ${phase.toUpperCase()}`;
  container.appendChild(phaseIndicator);
  
  return container;
}

/**
 * Create a voice characteristics visual component
 */
export function createVoiceVisual(emotion: string, personality: string, maturity: number): HTMLDivElement {
  const container = document.createElement('div');
  container.style.padding = 'var(--space-4)';
  
  // Emotion
  const emotionCard = createCard({
    title: 'Emotion',
    className: 'mb-4'
  });
  const emotionContent = document.createElement('div');
  emotionContent.innerHTML = `
    <div style="text-align: center;">
      <div class="text-2xl mb-2">${getEmotionEmoji(emotion)}</div>
      <div class="text-lg" style="color: var(--voice-emotion);">${emotion}</div>
    </div>
  `;
  emotionCard.querySelector('.card-body')?.appendChild(emotionContent);
  
  // Personality
  const personalityCard = createCard({
    title: 'Personality',
    className: 'mb-4'
  });
  const personalityContent = document.createElement('div');
  personalityContent.innerHTML = `
    <div style="text-align: center;">
      <div class="text-2xl mb-2">🎭</div>
      <div class="text-lg" style="color: var(--voice-personality);">${personality}</div>
    </div>
  `;
  personalityCard.querySelector('.card-body')?.appendChild(personalityContent);
  
  // Maturity
  const maturitySection = document.createElement('div');
  maturitySection.style.marginTop = 'var(--space-4)';
  maturitySection.appendChild(createProgressBar(maturity, 'Maturity', 'success'));
  
  container.appendChild(emotionCard);
  container.appendChild(personalityCard);
  container.appendChild(maturitySection);
  
  return container;
}

/**
 * Get emoji for emotion
 */
function getEmotionEmoji(emotion: string): string {
  const emotionEmojis: Record<string, string> = {
    'neutral': '😐',
    'happy': '😊',
    'sad': '😢',
    'excited': '🤩',
    'contemplative': '🤔',
    'curious': '🧐',
    'confident': '😎',
    'uncertain': '😅'
  };
  return emotionEmojis[emotion.toLowerCase()] || '😐';
}

/**
 * Create a scientific visualization component for gas diffusion (Fick's Law)
 */
export function createGasDiffusionVisual(o2Level: number, co2Level: number, partialPressures: {
  atmosphericO2: number;
  capillaryO2: number;
  atmosphericCO2: number;
  capillaryCO2: number;
}): HTMLDivElement {
  const container = document.createElement('div');
  container.style.padding = 'var(--space-4)';
  container.innerHTML = `
    <div style="margin-bottom: var(--space-4);">
      <h4 style="font-size: 0.875rem; font-weight: 600; margin-bottom: var(--space-2);">
        🔬 Fick's Law of Diffusion
      </h4>
      <div style="font-size: 0.75rem; color: var(--text-tertiary);">
        dV/dt = D_L × A × ΔP
      </div>
    </div>
    
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-3); margin-bottom: var(--space-4);">
      <div class="card" style="text-align: center;">
        <div class="text-xs text-tertiary" style="margin-bottom: var(--space-1);">Partial Pressure (mmHg)</div>
        <div style="font-size: 1.5rem; font-weight: bold; color: #0EA5E9;">
          O₂ Gradient
        </div>
        <div style="font-size: 0.875rem; margin-top: var(--space-2);">
          ${partialPressures.atmosphericO2.toFixed(1)} → ${partialPressures.capillaryO2.toFixed(1)}
        </div>
        <div style="margin-top: var(--space-2);">
          <div class="progress-bar" style="width: ${(partialPressures.capillaryO2 / partialPressures.atmosphericO2) * 100}%; background: #0EA5E9;"></div>
        </div>
      </div>
      
      <div class="card" style="text-align: center;">
        <div class="text-xs text-tertiary" style="margin-bottom: var(--space-1);">Partial Pressure (mmHg)</div>
        <div style="font-size: 1.5rem; font-weight: bold; color: #F59E0B;">
          CO₂ Gradient
        </div>
        <div style="font-size: 0.875rem; margin-top: var(--space-2);">
          ${partialPressures.capillaryCO2.toFixed(1)} → ${partialPressures.atmosphericCO2.toFixed(1)}
        </div>
        <div style="margin-top: var(--space-2);">
          <div class="progress-bar" style="width: ${(partialPressures.atmosphericCO2 / partialPressures.capillaryCO2) * 100}%; background: #F59E0B;"></div>
        </div>
      </div>
    </div>
    
    <div style="background: rgba(59, 130, 246, 0.1); padding: var(--space-3); border-radius: var(--radius-md); font-size: 0.75rem;">
      <strong>Diffusion Rate:</strong> ΔP × 0.1 mL/(min·mmHg) × Surface Area
    </div>
  `;
  return container;
}

/**
 * Create a visualization for evolutionary fitness landscape
 */
export function createFitnessVisualization(fitness: number, diversity: number, generations: number): HTMLDivElement {
  const container = document.createElement('div');
  container.style.padding = 'var(--space-4)';
  container.innerHTML = `
    <div style="margin-bottom: var(--space-4);">
      <h4 style="font-size: 0.875rem; font-weight: 600; margin-bottom: var(--space-2);">
        🧬 Genetic Algorithm Metrics
      </h4>
    </div>
    
    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: var(--space-3); margin-bottom: var(--space-4);">
      <div class="card" style="text-align: center;">
        <div class="text-xs text-tertiary">Fitness</div>
        <div style="font-size: 1.5rem; font-weight: bold; color: #10B981;">
          ${(fitness * 100).toFixed(1)}%
        </div>
        <div class="progress-bar" style="width: ${fitness * 100}%; background: #10B981; margin-top: var(--space-2);"></div>
      </div>
      
      <div class="card" style="text-align: center;">
        <div class="text-xs text-tertiary">Diversity</div>
        <div style="font-size: 1.5rem; font-weight: bold; color: #8B5CF6;">
          ${(diversity * 100).toFixed(1)}%
        </div>
        <div class="progress-bar" style="width: ${diversity * 100}%; background: #8B5CF6; margin-top: var(--space-2);"></div>
      </div>
      
      <div class="card" style="text-align: center;">
        <div class="text-xs text-tertiary">Generations</div>
        <div style="font-size: 1.5rem; font-weight: bold; color: #3B82F6;">
          ${generations}
        </div>
      </div>
    </div>
    
    <div style="background: rgba(34, 197, 94, 0.1); padding: var(--space-3); border-radius: var(--radius-md); font-size: 0.75rem;">
      <strong>Selection:</strong> Roulette Wheel (P(i) = F(i)/ΣF)
    </div>
  `;
  return container;
}

/**
 * Create a fractal dimension visualization
 */
export function createFractalDimensionVisual(dimension: number, nodeCount: number): HTMLDivElement {
  const container = document.createElement('div');
  container.style.padding = 'var(--space-4)';
  container.innerHTML = `
    <div style="margin-bottom: var(--space-4);">
      <h4 style="font-size: 0.875rem; font-weight: 600; margin-bottom: var(--space-2);">
        📐 Fractal Dimension (Box-Counting)
      </h4>
      <div style="font-size: 0.75rem; color: var(--text-tertiary);">
        D = log(N(ε)) / log(1/ε)
      </div>
    </div>
    
    <div class="card" style="text-align: center; margin-bottom: var(--space-4);">
      <div class="text-xs text-tertiary">Hausdorff-Besicovitch Dimension</div>
      <div style="font-size: 3rem; font-weight: bold; color: #A855F7; margin: var(--space-2) 0;">
        ${dimension.toFixed(3)}
      </div>
      <div style="font-size: 0.875rem; color: var(--text-tertiary);">
        Nodes: ${nodeCount} | Scale: 4 levels
      </div>
    </div>
    
    <div style="background: rgba(168, 85, 247, 0.1); padding: var(--space-3); border-radius: var(--radius-md); font-size: 0.75rem;">
      <strong>Method:</strong> Linear regression of box counts vs scale (log-log)
    </div>
  `;
  return container;
}

/**
 * Create a physics visualization for Verlet integration
 */
export function createPhysicsVisual(energy: number, velocity: number, forces: number): HTMLDivElement {
  const container = document.createElement('div');
  container.style.padding = 'var(--space-4)';
  container.innerHTML = `
    <div style="margin-bottom: var(--space-4);">
      <h4 style="font-size: 0.875rem; font-weight: 600; margin-bottom: var(--space-2);">
        ⚛️ Verlet Integration
      </h4>
      <div style="font-size: 0.75rem; color: var(--text-tertiary);">
        x(t+dt) = 2x(t) - x(t-dt) + a(t) × dt²
      </div>
    </div>
    
    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: var(--space-3);">
      <div class="card" style="text-align: center;">
        <div class="text-xs text-tertiary">Energy</div>
        <div style="font-size: 1.25rem; font-weight: bold; color: #F59E0B;">
          ${energy.toFixed(2)}
        </div>
      </div>
      
      <div class="card" style="text-align: center;">
        <div class="text-xs text-tertiary">Velocity</div>
        <div style="font-size: 1.25rem; font-weight: bold; color: #3B82F6;">
          ${velocity.toFixed(2)}
        </div>
      </div>
      
      <div class="card" style="text-align: center;">
        <div class="text-xs text-tertiary">Forces</div>
        <div style="font-size: 1.25rem; font-weight: bold; color: #EF4444;">
          ${forces.toFixed(2)}
        </div>
      </div>
    </div>
  `;
  return container;
}

/**
 * Create a transcendence visualization for self-discovery mechanisms
 */
export function createTranscendenceVisual(
    emergentTraits: string[],
    feedbackLoops: number,
    bootstrapBoost: number,
    carryingCapacity: number,
    currentDensity: number
): HTMLDivElement {
  const container = document.createElement('div');
  container.style.padding = 'var(--space-4)';
  
  let emergentTraitsHTML = '';
  if (emergentTraits.length > 0) {
    emergentTraitsHTML = '<div style="margin-top: var(--space-3); padding: var(--space-2); background: rgba(168, 85, 247, 0.1); border-radius: var(--radius-md); font-size: 0.75rem;">';
    emergentTraitsHTML += '<strong>✨ Discovered Emergent Traits:</strong><br>';
    emergentTraits.forEach(trait => {
      const emoji = getTraitEmoji(trait);
      emergentTraitsHTML += `${emoji} ${trait}<br>`;
    });
    emergentTraitsHTML += '</div>';
  }
  
  container.innerHTML = `
    <div style="margin-bottom: var(--space-4);">
      <h4 style="font-size: 0.875rem; font-weight: 600; margin-bottom: var(--space-2);">
        🚀 Transcendence Mechanisms
      </h4>
      <div style="font-size: 0.75rem; color: var(--text-tertiary);">
        Letting the creature find its own way to exceed limits
      </div>
    </div>
    
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-3); margin-bottom: var(--space-4);">
      <div class="card" style="text-align: center;">
        <div class="text-xs text-tertiary">Feedback Loops</div>
        <div style="font-size: 1.5rem; font-weight: bold; color: #EC4899;">
          ${feedbackLoops}
        </div>
        <div style="font-size: 0.75rem; color: var(--text-tertiary); margin-top: var(--space-1);">
          Self-amplifying
        </div>
      </div>
      
      <div class="card" style="text-align: center;">
        <div class="text-xs text-tertiary">Bootstrap Boost</div>
        <div style="font-size: 1.5rem; font-weight: bold; color: #F59E0B;">
          ${(bootstrapBoost * 100).toFixed(1)}%
        </div>
        <div style="font-size: 0.75rem; color: var(--text-tertiary); margin-top: var(--space-1);">
          Self-enhancement
        </div>
      </div>
    </div>
    
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-3); margin-bottom: var(--space-4);">
      <div class="card" style="text-align: center;">
        <div class="text-xs text-tertiary">Carrying Capacity</div>
        <div style="font-size: 1.25rem; font-weight: bold; color: #10B981;">
          ${carryingCapacity.toFixed(1)}
        </div>
        <div class="progress-bar" style="width: ${currentDensity * 100}%; background: #10B981; margin-top: var(--space-2);"></div>
        <div style="font-size: 0.75rem; margin-top: var(--space-1);">
          ${(currentDensity * 100).toFixed(1)}% capacity
        </div>
      </div>
      
      <div class="card" style="text-align: center;">
        <div class="text-xs text-tertiary">Transcendence Level</div>
        <div style="font-size: 1.25rem; font-weight: bold; color: #A855F7;">
          ${(bootstrapBoost * emergentTraits.length * 10).toFixed(0)}%
        </div>
        <div style="font-size: 0.75rem; color: var(--text-tertiary); margin-top: var(--space-1);">
          Beyond initial limits
        </div>
      </div>
    </div>
    
    ${emergentTraitsHTML}
    
    <div style="background: rgba(236, 72, 153, 0.1); padding: var(--space-3); border-radius: var(--radius-md); font-size: 0.75rem; margin-top: var(--space-3);">
      <strong>Self-Discovery:</strong> Creature discovers new paths through emergent behavior and self-amplifying feedback loops
    </div>
  `;
  return container;
}

function getTraitEmoji(trait: string): string {
  const emojis: Record<string, string> = {
    'meta_evolution': '🔄',
    'synergistic_efficiency': '⚡',
    'recursive_adaptation': '🌀',
    'adaptive_breathing': '🌬️',
    'efficient_breathing': '🌪️'
  };
  return emojis[trait] || '✨';
}

/**
 * Log Panel Component - displays console logs in a corner panel
 */
export interface LogEntry {
  timestamp: number;
  level: 'log' | 'error' | 'warn' | 'info';
  message: string;
  args?: any[];
}

export class LogPanel {
  private container!: HTMLElement;
  private logs: LogEntry[] = [];
  private maxLogs: number = 200;
  private logLevel: 'all' | 'error' = 'all';
  private originalConsole: { log: (...args: any[]) => void; error: (...args: any[]) => void; warn: (...args: any[]) => void; info: (...args: any[]) => void } = null!;
  
  // Performance optimization: throttle updates and track visibility
  private isVisible: boolean = false;
  private lastUpdateTime: number = 0;
  private updateThrottle: number = 100; // Only update every 100ms
  private pendingUpdate: boolean = false;
  
  constructor() {
    this.initializeLogPanel();
    this.captureConsole();
  }
  
  private initializeLogPanel() {
    // Create main container
    this.container = document.createElement('div');
    this.container.className = 'log-panel';
    this.container.style.position = 'fixed';
    this.container.style.bottom = '16px';
    this.container.style.right = '16px';
    this.container.style.width = '420px';
    this.container.style.maxHeight = '400px';
    this.container.style.background = 'var(--color-bg-secondary)';
    this.container.style.border = '1px solid var(--color-border)';
    this.container.style.borderRadius = 'var(--radius-md)';
    this.container.style.boxShadow = 'var(--shadow-xl)';
    this.container.style.zIndex = '10000';
    this.container.style.display = 'flex';
    this.container.style.flexDirection = 'column';
    this.container.style.fontFamily = 'var(--font-mono)';
    this.container.style.fontSize = 'var(--font-size-xs)';
    
    // Create header
    const header = document.createElement('div');
    header.style.padding = 'var(--space-3)';
    header.style.borderBottom = '1px solid var(--color-border)';
    header.style.display = 'flex';
    header.style.justifyContent = 'space-between';
    header.style.alignItems = 'center';
    
    const title = document.createElement('div');
    title.innerHTML = '📋 Console Logs';
    title.style.fontWeight = 'bold';
    header.appendChild(title);
    
    // Create button container
    const buttonContainer = document.createElement('div');
    buttonContainer.style.display = 'flex';
    buttonContainer.style.gap = 'var(--space-2)';
    
    // Copy all button
    const copyAllBtn = document.createElement('button');
    copyAllBtn.innerHTML = '📋 All';
    copyAllBtn.className = 'btn btn-ghost btn-small';
    copyAllBtn.onclick = () => this.copyAll();
    buttonContainer.appendChild(copyAllBtn);
    
    // Copy errors button
    const copyErrorsBtn = document.createElement('button');
    copyErrorsBtn.innerHTML = '⚠️ Errors';
    copyErrorsBtn.className = 'btn btn-ghost btn-small';
    copyErrorsBtn.onclick = () => this.copyErrors();
    buttonContainer.appendChild(copyErrorsBtn);
    
    // Toggle level button
    const toggleLevelBtn = document.createElement('button');
    toggleLevelBtn.innerHTML = '🔄 All';
    toggleLevelBtn.className = 'btn btn-ghost btn-small';
    toggleLevelBtn.onclick = () => {
      this.logLevel = this.logLevel === 'all' ? 'error' : 'all';
      toggleLevelBtn.innerHTML = this.logLevel === 'all' ? '🔄 All' : '🔄 Errors';
      this.updateDisplay();
    };
    buttonContainer.appendChild(toggleLevelBtn);
    
    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '✕';
    closeBtn.className = 'btn btn-ghost btn-small';
    closeBtn.onclick = () => {
      this.container.style.display = 'none';
    };
    buttonContainer.appendChild(closeBtn);
    
    header.appendChild(buttonContainer);
    this.container.appendChild(header);
    
    // Create log content area
    const logContent = document.createElement('div');
    logContent.id = 'log-content';
    logContent.style.flex = '1';
    logContent.style.overflowY = 'auto';
    logContent.style.overflowX = 'hidden';
    logContent.style.padding = 'var(--space-2)';
    logContent.style.maxHeight = '320px';
    logContent.className = 'scrollbar-thin';
    this.container.appendChild(logContent);
    
    // Add to document
    document.body.appendChild(this.container);
  }
  
  private captureConsole() {
    this.originalConsole = {
      log: console.log.bind(console),
      error: console.error.bind(console),
      warn: console.warn.bind(console),
      info: console.info.bind(console)
    };
    
    const self = this;
    
    console.log = function(...args: any[]) {
      self.originalConsole.log.apply(console, args);
      self.addLog('log', args);
    };
    
    console.error = function(...args: any[]) {
      self.originalConsole.error.apply(console, args);
      self.addLog('error', args);
    };
    
    console.warn = function(...args: any[]) {
      self.originalConsole.warn.apply(console, args);
      self.addLog('warn', args);
    };
    
    console.info = function(...args: any[]) {
      self.originalConsole.info.apply(console, args);
      self.addLog('info', args);
    };
  }
  
  private addLog(level: LogEntry['level'], args: any[]) {
    const message = args.map(arg => 
      typeof arg === 'object' ? JSON.stringify(arg, null, 0) : String(arg)
    ).join(' ');
    
    this.logs.push({
      timestamp: Date.now(),
      level,
      message,
      args
    });

    // Keep non-error logs bounded for performance while preserving errors.
    this.enforceLogRetention();
    
    // PERFORMANCE FIX: Only update display if panel is visible
    if (this.isVisible) {
      this.scheduleUpdate();
    }
  }

  private enforceLogRetention() {
    let nonErrorCount = 0;
    for (const log of this.logs) {
      if (log.level !== 'error') {
        nonErrorCount++;
      }
    }

    const overflow = nonErrorCount - this.maxLogs;
    if (overflow <= 0) {
      return;
    }

    let removed = 0;
    this.logs = this.logs.filter(log => {
      if (removed < overflow && log.level !== 'error') {
        removed++;
        return false;
      }
      return true;
    });
  }
  
  private scheduleUpdate() {
    const now = Date.now();
    
    // If enough time has passed since last update, update immediately
    if (now - this.lastUpdateTime >= this.updateThrottle) {
      this.updateDisplay();
      this.lastUpdateTime = now;
      this.pendingUpdate = false;
    } else if (!this.pendingUpdate) {
      // Otherwise, schedule a delayed update
      this.pendingUpdate = true;
      setTimeout(() => {
        if (this.pendingUpdate && this.isVisible) {
          this.updateDisplay();
          this.lastUpdateTime = Date.now();
          this.pendingUpdate = false;
        }
      }, this.updateThrottle - (now - this.lastUpdateTime));
    }
  }
  
  private updateDisplay() {
    const logContent = document.getElementById('log-content');
    if (!logContent || !this.isVisible) return;
    
    const filteredLogs = this.logLevel === 'error' 
      ? this.logs.filter(log => log.level === 'error' || log.level === 'warn')
      : this.logs;
    
    // PERFORMANCE FIX: Limit number of displayed logs and use efficient innerHTML update
    const logsToDisplay = filteredLogs.slice(-50); // Reduced from 100 to 50
    
    logContent.innerHTML = logsToDisplay.map(log => {
      const time = new Date(log.timestamp).toLocaleTimeString();
      const levelColor = this.getLevelColor(log.level);
      return `<div style="margin-bottom:2px;padding:2px 4px;color:${levelColor};border-left:2px solid ${levelColor};padding-left:6px;"><span style="opacity:0.6;font-size:0.85em;">[${time}]</span> ${this.escape(log.message)}</div>`;
    }).join('');
    
    // Auto-scroll to bottom
    logContent.scrollTop = logContent.scrollHeight;
  }
  
  // Helper to escape HTML in log messages
  private escape(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
  
  private getLevelColor(level: LogEntry['level']): string {
    switch (level) {
      case 'error': return '#ef4444';
      case 'warn': return '#f59e0b';
      case 'info': return '#3b82f6';
      default: return 'var(--color-text-secondary)';
    }
  }
  
  private copyAll() {
    const text = this.logs.map(log => {
      const time = new Date(log.timestamp).toLocaleString();
      return `[${time}] [${log.level.toUpperCase()}] ${log.message}`;
    }).join('\n');
    
    navigator.clipboard.writeText(text).then(() => {
      this.showCopyNotification('All logs copied');
    });
  }
  
  private copyErrors() {
    const errors = this.logs.filter(log => log.level === 'error' || log.level === 'warn');
    const text = errors.map(log => {
      const time = new Date(log.timestamp).toLocaleString();
      return `[${time}] [${log.level.toUpperCase()}] ${log.message}`;
    }).join('\n');
    
    if (text) {
      navigator.clipboard.writeText(text).then(() => {
        this.showCopyNotification(`${errors.length} errors copied`);
      });
    } else {
      this.showCopyNotification('No errors found');
    }
  }
  
  private showCopyNotification(message: string) {
    const notif = document.createElement('div');
    notif.textContent = message;
    notif.style.position = 'fixed';
    notif.style.bottom = '440px';
    notif.style.right = '16px';
    notif.style.background = 'var(--color-primary)';
    notif.style.color = '#fff';
    notif.style.padding = '8px 16px';
    notif.style.borderRadius = 'var(--radius-md)';
    notif.style.zIndex = '10001';
    document.body.appendChild(notif);
    
    setTimeout(() => {
      notif.style.opacity = '0';
      notif.style.transition = 'opacity 0.3s';
      setTimeout(() => notif.remove(), 300);
    }, 2000);
  }
  
  public show() {
    this.isVisible = true;
    this.container.style.display = 'flex';
    // Update display when showing the panel
    this.updateDisplay();
  }
  
  public hide() {
    this.isVisible = false;
    this.container.style.display = 'none';
  }
}

